import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Card, Empty, Image, Input, Spin, Typography, message } from 'antd'
import { env } from '../config/environment'

const { Title, Text, Paragraph } = Typography

const API_URL = env.apiBaseUrl.replace(/\/api$/, '')

type PublicPayload = {
  mode: string
  job: { id: string; jobNumber: string; title: string; customerName: string | null }
  businessName: string
  photos: Array<{
    id: string
    url: string | null
    originalName: string
    jobPhotoCategory: string | null
    phase: string | null
    createdAt: string
    capturedAt: string | null
  }>
  report: {
    id: string
    title: string
    type: string
    sections: Array<{
      id: string
      kind: string
      title?: string
      body?: string
      photoIds?: string[]
    }>
    status: string
  } | null
}

export function PublicPhotoDocs() {
  const { token } = useParams<{ token: string }>()
  const [loading, setLoading] = useState(true)
  const [passwordRequired, setPasswordRequired] = useState(false)
  const [password, setPassword] = useState('')
  const [data, setData] = useState<PublicPayload | null>(null)

  const load = async (pwd?: string) => {
    if (!token) return
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/public/photo-docs/${token}`, {
        method: pwd ? 'POST' : 'GET',
        headers: pwd ? { 'Content-Type': 'application/json' } : undefined,
        body: pwd ? JSON.stringify({ password: pwd }) : undefined,
      })
      const json = await res.json().catch(() => ({}))
      if (res.status === 401 && json.code === 'PASSWORD_REQUIRED') {
        setPasswordRequired(true)
        setData(null)
        return
      }
      if (res.status === 403 || json.code === 'CAMERA_FLOW_REQUIRED') {
        setPasswordRequired(false)
        setData(null)
        message.error(
          json.error ||
            'This photo documentation link is unavailable. CameraFlow is not active for this business.'
        )
        return
      }
      if (!res.ok) {
        message.error(json.error || 'Share link unavailable')
        setData(null)
        return
      }
      setPasswordRequired(false)
      setData(json.data || json)
    } catch {
      message.error('Failed to load photo documentation')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (passwordRequired) {
    return (
      <div style={{ maxWidth: 420, margin: '64px auto', padding: 24 }}>
        <Card title="Password required">
          <Paragraph>This photo share is password protected.</Paragraph>
          <Input.Password
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            style={{ marginBottom: 12 }}
          />
          <Button type="primary" block onClick={() => load(password)}>
            View
          </Button>
        </Card>
      </div>
    )
  }

  if (!data) {
    return (
      <div style={{ padding: 48 }}>
        <Empty description="This photo documentation is unavailable or the share link expired" />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      <Title level={3}>{data.businessName}</Title>
      <Text type="secondary">
        {data.job.jobNumber} — {data.job.title}
        {data.job.customerName ? ` · ${data.job.customerName}` : ''}
      </Text>

      {data.report ? (
        <Card style={{ marginTop: 24 }} title={data.report.title}>
          {data.report.sections.map((s) => (
            <div key={s.id} style={{ marginBottom: 16 }}>
              {s.kind === 'heading' ? <Title level={4}>{s.title}</Title> : null}
              {s.kind === 'text' ? <Paragraph>{s.body}</Paragraph> : null}
              {s.kind === 'photos' ? (
                <>
                  {s.title ? <Text strong>{s.title}</Text> : null}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: 12,
                      marginTop: 8,
                    }}
                  >
                    {(s.photoIds || []).map((id) => {
                      const p = data.photos.find((x) => x.id === id)
                      return p?.url ? (
                        <Image key={id} src={p.url} alt={p.originalName} style={{ borderRadius: 8 }} />
                      ) : null
                    })}
                  </div>
                </>
              ) : null}
            </div>
          ))}
        </Card>
      ) : (
        <Card style={{ marginTop: 24 }} title={data.mode === 'timeline' ? 'Timeline' : 'Gallery'}>
          {data.photos.length === 0 ? (
            <Empty description="No photos yet" />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 12,
              }}
            >
              {data.photos.map((p) => (
                <div key={p.id}>
                  {p.url ? (
                    <Image
                      src={p.url}
                      alt={p.originalName}
                      style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 8 }}
                    />
                  ) : null}
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {[p.phase, p.jobPhotoCategory].filter(Boolean).join(' · ')}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
